import { useEffect, useRef, useState } from 'react';
import IconAdd from '../../../components/icons/IconAdd';
import { createMarker, updateMarker, deleteMarker } from './blockMarkersApi';
import { BLOCK_MARKER_TYPE_LIST, getBlockMarkerType } from './blockMarkerTypes';
import language from '../../../variables/language';
import '../../../css/lesson-topic-link.css';
import '../../../css/block-marker.css';

// Wraps each of the 3 top-level topic timeline entries (TopicBlockEntry /
// TopicContainerGroup / ExerciseLinkSection) with a teacher-settable marker
// icon living in the widened gutter to the block's left (see
// .course-workspace-body's gap in courses.css) - vertically centered
// against whatever height the wrapped block ends up rendering at, via a
// plain position:relative/absolute pair rather than measuring anything in
// JS. At most one marker per target (kind+targetId), matching the backend's
// one-marker-per-target model - picking a different type PUTs over the
// existing marker instead of creating a second one.
export default function BlockMarkerSlot({ kind, targetId, marker, workspaceId, onChanged, children }) {
   const [open, setOpen] = useState(false);
   const [saving, setSaving] = useState(false);
   const wrapRef = useRef(null);
   const lang = language[language.lang];

   useEffect(() => {
      const handleOutside = (e) => {
         if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
      };
      document.addEventListener('mousedown', handleOutside);
      return () => document.removeEventListener('mousedown', handleOutside);
   }, []);

   const pickType = (code) => {
      if (saving) return;
      setSaving(true);
      const request = marker ? updateMarker(marker, code) : createMarker(workspaceId, kind, targetId, code);
      request.then(() => { setOpen(false); if (onChanged) onChanged(); })
         .catch(() => {}).finally(() => setSaving(false));
   };

   const removeMarker = (e) => {
      e.stopPropagation();
      if (!marker || saving) return;
      setSaving(true);
      deleteMarker(marker.id).then(() => { setOpen(false); if (onChanged) onChanged(); })
         .catch(() => {}).finally(() => setSaving(false));
   };

   const activeType = marker ? getBlockMarkerType(marker.markerType) : null;

   return (
      <div className='workspace-block-marker-slot' ref={wrapRef}>
         {marker && activeType ? (
            <>
               <span className='workspace-block-marker-bar' style={{ background: activeType.color }} />
               <span className='workspace-block-marker-icon-wrap' style={{ color: activeType.color }}
                  onClick={() => setOpen(v => !v)} title={lang[activeType.labelKey]}>
                  <activeType.Icon />
               </span>
            </>
         ) : (
            <span className='workspace-block-marker-add-trigger' onClick={() => setOpen(v => !v)} title={lang.blockMarkerAdd}>
               <IconAdd style={{ width: 18, height: 18 }} />
            </span>
         )}
         {open ? (
            <div className='lesson-topic-flyout workspace-block-marker-flyout' onMouseDown={e => e.stopPropagation()}>
               <p className='lesson-topic-flyout-title'>{lang.blockMarkerAdd}</p>
               <div className='lesson-topic-option-list'>
                  {BLOCK_MARKER_TYPE_LIST.map(t => (
                     <div key={t.code} className={'lesson-topic-option workspace-block-marker-option' + (marker && marker.markerType === t.code ? ' active' : '')}
                        onClick={() => pickType(t.code)}>
                        <span className='workspace-block-marker-option-icon' style={{ color: t.color }}><t.Icon /></span>
                        {lang[t.labelKey]}
                     </div>
                  ))}
               </div>
               {marker ? (
                  <span className='lesson-homework-create-exercise workspace-block-marker-remove' onClick={removeMarker}>
                     {lang.blockMarkerRemove}
                  </span>
               ) : ''}
            </div>
         ) : ''}
         {children}
      </div>
   );
}
